/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { format } from 'date-fns'

import Container from '@mui/material/Container'
import Typography from '@mui/material/Typography'
import Card from '@mui/material/Card'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Unstable_Grid2'
import Paper from '@mui/material/Paper'
import Chip from '@mui/material/Chip'
import Button from '@mui/material/Button'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import Skeleton from '@mui/material/Skeleton'

import { masks } from 'src/utils/masks'
import { fNumber, fPercent } from 'src/utils/format-number'
import axiosInstance from 'src/utils/axios'
import { useBoolean } from 'src/hooks/use-boolean'
import useDebounce from 'src/hooks/use-debounce'
import useEvents from 'src/service/events.service'
import { OptionType } from 'src/components/hook-form/rhf-autocomplete'
import Iconify from 'src/components/iconify'
import DateTimeRangePicker from 'src/components/date-time-range-picker'

import SellerTable, { SellerRow } from './seller-table'
import SellerShareChart from './seller-share-chart'
import SellerDetailDialog from './seller-detail-dialog'

export default function SellerPerformance() {
  const isLoading = useBoolean(false)
  const [query, setQuery] = useState('')
  const [eventFilter, setEventFilter] = useState<any>()
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const searchDebounce = useDebounce(query, 500)

  const [performanceData, setPerformanceData] = useState<{
    performanceAnalysis: SellerRow[]
    currentRanking?: any[]
  } | null>(null)

  const [selectedSeller, setSelectedSeller] = useState<SellerRow | null>(null)

  const { isLoading: loadingEvent, data: dataEvent } = useEvents(
    { page: 1, perPage: 100, sort: undefined },
    { search: searchDebounce },
  )

  const fetchPerformanceData = async (event?: string) => {
    const eventId = event || eventFilter?.value
    if (!eventId) return

    try {
      isLoading.onTrue()
      const { data: response } = await axiosInstance.get(`/dashboard/seller-performance`, {
        params: {
          event: eventId,
          startDate: startDate ? format(startDate, "yyyy-MM-dd'T'HH:mm:ss.SSSxxx") : undefined,
          endDate: endDate ? format(endDate, "yyyy-MM-dd'T'HH:mm:ss.SSSxxx") : undefined,
        },
      })
      setPerformanceData(response)
    } catch {
      // ignore
    } finally {
      isLoading.onFalse()
    }
  }

  const handleFilterEvent = (event: any, selectedValue: any) => {
    setEventFilter(selectedValue)
    if (selectedValue?.value) {
      fetchPerformanceData(selectedValue.value)
    } else {
      setPerformanceData(null)
    }
  }

  const handleRefresh = () => fetchPerformanceData()

  useEffect(() => {
    if (eventFilter?.value) fetchPerformanceData()
  }, [startDate, endDate])

  const sellers = performanceData?.performanceAnalysis || []

  const stats = useMemo(() => {
    if (!sellers.length) {
      return {
        totalSales: 0,
        totalOrders: 0,
        avgTicket: 0,
        topSeller: null as SellerRow | null,
        totalNet: 0,
      }
    }
    const totalSales = sellers.reduce((a, s) => a + (s.totalSales || 0), 0)
    const totalOrders = sellers.reduce((a, s) => a + (s.totalOrders || 0), 0)
    const totalNet = sellers.reduce((a, s) => a + (s.netSales || 0), 0)
    const avgTicket = totalOrders ? totalSales / totalOrders : 0
    const topSeller = [...sellers].sort((a, b) => b.totalSales - a.totalSales)[0]
    return { totalSales, totalOrders, avgTicket, topSeller, totalNet }
  }, [sellers])

  return (
    <>
      <Helmet>
        <title>Dashboard: Performance de Vendedores</title>
      </Helmet>

      <Container maxWidth='xl'>
        {/* HEADER COMPACTO */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: 2,
            px: { xs: 2, sm: 2.5 },
            py: { xs: 1.5, sm: 2 },
            mb: { xs: 2, md: 3 },
            color: 'white',
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent='space-between'
            spacing={1.5}
          >
            <Stack direction='row' alignItems='center' spacing={1.5}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1.5,
                  bgcolor: 'rgba(255,255,255,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Iconify icon='solar:users-group-two-rounded-bold' width={22} />
              </Box>
              <Box>
                <Typography variant='h6' fontWeight={700} sx={{ fontSize: { xs: 16, sm: 18 } }}>
                  Performance de Vendedores
                </Typography>
                <Typography
                  variant='caption'
                  sx={{ opacity: 0.9, fontSize: { xs: 11.5, sm: 12.5 } }}
                >
                  Ranking, métricas e análise detalhada por vendedor
                </Typography>
              </Box>
            </Stack>

            <Stack direction='row' alignItems='center' spacing={1}>
              {sellers.length > 0 && (
                <Stack direction='row' spacing={1}>
                  <MiniKpi
                    icon='solar:dollar-minimalistic-bold'
                    label='Faturamento'
                    value={masks.currencyAllPlatforms(stats.totalSales)}
                  />
                  <MiniKpi
                    icon='solar:users-group-rounded-bold'
                    label='Vendedores'
                    value={fNumber(sellers.length)}
                  />
                </Stack>
              )}
              <Button
                onClick={handleRefresh}
                disabled={isLoading.value || !eventFilter?.value}
                size='small'
                variant='contained'
                color='inherit'
                startIcon={
                  <Iconify
                    icon='solar:refresh-bold'
                    width={16}
                    sx={{
                      animation: isLoading.value ? 'spin 1s linear infinite' : 'none',
                      '@keyframes spin': {
                        '0%': { transform: 'rotate(0deg)' },
                        '100%': { transform: 'rotate(360deg)' },
                      },
                    }}
                  />
                }
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                }}
              >
                Atualizar
              </Button>
            </Stack>
          </Stack>
        </Box>

        <Stack spacing={2.5}>
          {/* FILTROS */}
          <Card sx={{ borderRadius: 2 }}>
            <Box
              sx={{
                px: 2,
                py: 1.25,
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.neutral',
              }}
            >
              <Stack direction='row' alignItems='center' spacing={1}>
                <Iconify icon='solar:filter-bold' width={16} sx={{ color: 'primary.main' }} />
                <Typography variant='subtitle2' fontWeight={700} sx={{ fontSize: 13 }}>
                  Filtros
                </Typography>
                <Typography variant='caption' color='text.secondary' sx={{ fontSize: 11.5 }}>
                  · Selecione um evento e o período para começar
                </Typography>
              </Stack>
            </Box>
            <Box sx={{ p: 2 }}>
              <Grid container spacing={1.5}>
                <Grid xs={12}>
                  <DateTimeRangePicker
                    label='Período'
                    size='small'
                    startDate={startDate}
                    endDate={endDate}
                    onChange={(s, e) => {
                      setStartDate(s)
                      setEndDate(e)
                    }}
                  />
                </Grid>
                <Grid xs={12}>
                  <Autocomplete
                    size='small'
                    options={
                      dataEvent?.data?.map((option) => ({
                        label: option?.name,
                        value: option?._id,
                      })) || []
                    }
                    isOptionEqualToValue={(option: any, value: any) =>
                      option?.value === value?.value
                    }
                    getOptionLabel={(option: OptionType | string) => (option as OptionType)?.label}
                    onChange={handleFilterEvent}
                    value={eventFilter}
                    loading={loadingEvent}
                    inputValue={query}
                    onInputChange={(e, newInputValue) => setQuery(newInputValue)}
                    renderInput={(params) => (
                      <TextField
                        label='Evento'
                        placeholder='Selecione um evento'
                        {...params}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Box>
          </Card>

          {(() => {
            if (!eventFilter?.value) return <EmptyState />
            if (isLoading.value) return <LoadingState />
            if (!performanceData || !sellers.length) return <NoDataState />

            return (
              <>
                {/* KPI CARDS */}
                <Grid container spacing={1.5}>
                  <KpiCard
                    icon='solar:dollar-minimalistic-bold'
                    color='#10b981'
                    label='Faturamento Total'
                    value={masks.currencyAllPlatforms(stats.totalSales)}
                    sub={`${fNumber(stats.totalOrders)} pedidos · líquido ${masks.currencyAllPlatforms(stats.totalNet)}`}
                  />
                  <KpiCard
                    icon='solar:cart-large-2-bold'
                    color='#6366f1'
                    label='Ticket Médio Geral'
                    value={masks.currencyAllPlatforms(stats.avgTicket)}
                    sub='Valor médio por pedido no evento'
                  />
                  <KpiCard
                    icon='solar:users-group-rounded-bold'
                    color='#8b5cf6'
                    label='Vendedores Ativos'
                    value={fNumber(sellers.length)}
                    sub={`${fNumber(
                      Math.round(stats.totalOrders / Math.max(sellers.length, 1)),
                    )} pedidos/vendedor (média)`}
                  />
                  <KpiCard
                    icon='solar:crown-bold'
                    color='#f59e0b'
                    label='Top Vendedor'
                    value={stats.topSeller?.sellerName || '—'}
                    sub={
                      stats.topSeller
                        ? `${masks.currencyAllPlatforms(
                            stats.topSeller.totalSales,
                          )} · ${fPercent(
                            stats.totalSales
                              ? (stats.topSeller.totalSales / stats.totalSales) * 100
                              : 0,
                          )} do total`
                        : '—'
                    }
                  />
                </Grid>

                {/* DONUT + INSIGHTS */}
                <Grid container spacing={2}>
                  <Grid xs={12} md={7}>
                    <SellerShareChart data={sellers} />
                  </Grid>
                  <Grid xs={12} md={5}>
                    <InsightsCard sellers={sellers} totalSales={stats.totalSales} />
                  </Grid>
                </Grid>

                {/* TABELA */}
                <SellerTable data={sellers} onOpenDetail={setSelectedSeller} />
              </>
            )
          })()}
        </Stack>
      </Container>

      <SellerDetailDialog
        open={!!selectedSeller}
        seller={selectedSeller}
        totalSalesAll={stats.totalSales}
        onClose={() => setSelectedSeller(null)}
      />
    </>
  )
}

// ----------------------------------------------------------------------

function MiniKpi({ icon, label, value }: { icon: string; label: string; value: string }) {
  return (
    <Box
      sx={{
        bgcolor: 'rgba(255,255,255,0.15)',
        borderRadius: 1.25,
        px: 1.25,
        py: 0.5,
        display: { xs: 'none', md: 'block' },
      }}
    >
      <Stack direction='row' alignItems='center' spacing={0.75}>
        <Iconify icon={icon} width={14} sx={{ opacity: 0.9 }} />
        <Box>
          <Typography sx={{ fontSize: 10, opacity: 0.85, lineHeight: 1 }}>{label}</Typography>
          <Typography sx={{ fontSize: 12.5, fontWeight: 700, lineHeight: 1.3 }}>{value}</Typography>
        </Box>
      </Stack>
    </Box>
  )
}

// ----------------------------------------------------------------------

function KpiCard({
  icon,
  color,
  label,
  value,
  sub,
}: {
  icon: string
  color: string
  label: string
  value: string
  sub?: string
}) {
  return (
    <Grid xs={12} sm={6} md={3}>
      <Paper
        sx={{
          p: 2,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          height: '100%',
          transition: 'all 0.2s',
          '&:hover': {
            borderColor: `${color}55`,
            boxShadow: `0 4px 12px ${color}22`,
          },
        }}
      >
        <Stack direction='row' alignItems='center' spacing={1.25}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.25,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: `${color}1a`,
              color,
            }}
          >
            <Iconify icon={icon} width={18} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant='caption'
              color='text.secondary'
              sx={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3 }}
            >
              {label}
            </Typography>
            <Typography
              variant='subtitle1'
              fontWeight={700}
              sx={{ fontSize: 16, lineHeight: 1.3, mt: 0.25 }}
              noWrap
            >
              {value}
            </Typography>
            {sub ? (
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ fontSize: 10.5, lineHeight: 1.3, display: 'block' }}
                noWrap
              >
                {sub}
              </Typography>
            ) : null}
          </Box>
        </Stack>
      </Paper>
    </Grid>
  )
}

// ----------------------------------------------------------------------

function InsightsCard({ sellers, totalSales }: { sellers: SellerRow[]; totalSales: number }) {
  const sorted = useMemo(
    () => [...sellers].sort((a, b) => b.totalSales - a.totalSales),
    [sellers],
  )

  const topItem = useMemo(
    () => [...sellers].sort((a, b) => (b.totalItems || 0) - (a.totalItems || 0))[0],
    [sellers],
  )

  const topTicket = useMemo(
    () => [...sellers].sort((a, b) => (b.averageTicket || 0) - (a.averageTicket || 0))[0],
    [sellers],
  )

  const topVariety = useMemo(
    () => [...sellers].sort((a, b) => (b.uniqueProductsCount || 0) - (a.uniqueProductsCount || 0))[0],
    [sellers],
  )

  const top3Share = useMemo(() => {
    if (!totalSales) return 0
    return (sorted.slice(0, 3).reduce((a, s) => a + s.totalSales, 0) / totalSales) * 100
  }, [sorted, totalSales])

  return (
    <Card sx={{ borderRadius: 2, height: '100%' }}>
      <Box
        sx={{
          px: 2,
          py: 1.25,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.neutral',
        }}
      >
        <Stack direction='row' alignItems='center' spacing={1}>
          <Iconify icon='solar:medal-star-bold' width={18} sx={{ color: 'warning.main' }} />
          <Typography variant='subtitle2' fontWeight={700} sx={{ fontSize: 13 }}>
            Destaques
          </Typography>
        </Stack>
      </Box>

      <Stack divider={<Box sx={{ borderTop: '1px dashed', borderColor: 'divider' }} />}>
        <InsightRow
          icon='solar:cup-star-bold'
          color='#f59e0b'
          label='Maior faturamento'
          name={sorted[0]?.sellerName}
          value={
            sorted[0] ? masks.currencyAllPlatforms(sorted[0].totalSales) : '—'
          }
        />
        <InsightRow
          icon='solar:box-bold'
          color='#10b981'
          label='Mais itens vendidos'
          name={topItem?.sellerName}
          value={topItem?.totalItems ? `${fNumber(topItem.totalItems)} itens` : '—'}
        />
        <InsightRow
          icon='solar:cart-check-bold'
          color='#6366f1'
          label='Maior ticket médio'
          name={topTicket?.sellerName}
          value={
            topTicket?.averageTicket
              ? masks.currencyAllPlatforms(topTicket.averageTicket)
              : '—'
          }
        />
        <InsightRow
          icon='solar:widget-5-bold'
          color='#a855f7'
          label='Mais variedade'
          name={topVariety?.sellerName}
          value={
            topVariety?.uniqueProductsCount
              ? `${fNumber(topVariety.uniqueProductsCount)} produtos`
              : '—'
          }
        />
        <Box sx={{ p: 2, bgcolor: 'background.neutral' }}>
          <Stack direction='row' alignItems='center' spacing={1.25}>
            <Iconify icon='solar:graph-up-bold' width={20} sx={{ color: 'info.main' }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant='caption' color='text.secondary' sx={{ fontSize: 11 }}>
                Concentração nos Top 3
              </Typography>
              <Typography variant='subtitle2' fontWeight={700} sx={{ fontSize: 14 }}>
                {fPercent(top3Share)}{' '}
                <Typography
                  component='span'
                  variant='caption'
                  color='text.secondary'
                  sx={{ fontSize: 11, fontWeight: 500 }}
                >
                  do faturamento total
                </Typography>
              </Typography>
            </Box>
          </Stack>
        </Box>
      </Stack>
    </Card>
  )
}

function InsightRow({
  icon,
  color,
  label,
  name,
  value,
}: {
  icon: string
  color: string
  label: string
  name?: string
  value: string
}) {
  return (
    <Stack direction='row' alignItems='center' spacing={1.5} sx={{ px: 2, py: 1.25 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: `${color}1a`,
          color,
          flexShrink: 0,
        }}
      >
        <Iconify icon={icon} width={16} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant='caption' color='text.secondary' sx={{ fontSize: 11 }}>
          {label}
        </Typography>
        <Typography variant='body2' fontWeight={600} sx={{ fontSize: 13 }} noWrap>
          {name || '—'}
        </Typography>
      </Box>
      <Typography variant='body2' fontWeight={700} sx={{ fontSize: 13, color }}>
        {value}
      </Typography>
    </Stack>
  )
}

// ----------------------------------------------------------------------

function EmptyState() {
  return (
    <Card
      sx={{
        p: { xs: 4, md: 6 },
        borderRadius: 2,
        textAlign: 'center',
        background:
          'linear-gradient(135deg, rgba(99,102,241,0.04) 0%, rgba(139,92,246,0.04) 100%)',
        border: '2px dashed',
        borderColor: 'primary.light',
      }}
    >
      <Iconify
        icon='solar:users-group-rounded-bold-duotone'
        width={64}
        sx={{ color: 'primary.main', opacity: 0.6, mb: 1.5 }}
      />
      <Typography variant='h6' gutterBottom>
        Selecione um evento
      </Typography>
      <Typography variant='body2' color='text.secondary' sx={{ maxWidth: 420, mx: 'auto' }}>
        Escolha um evento no filtro acima para visualizar a performance dos vendedores, ranking
        e análise detalhada.
      </Typography>
    </Card>
  )
}

function NoDataState() {
  return (
    <Card
      sx={{
        p: { xs: 4, md: 6 },
        borderRadius: 2,
        textAlign: 'center',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Iconify
        icon='solar:database-bold-duotone'
        width={56}
        sx={{ color: 'text.disabled', mb: 1.5 }}
      />
      <Typography variant='subtitle1' fontWeight={700} gutterBottom>
        Sem dados de vendas no período
      </Typography>
      <Typography variant='body2' color='text.secondary'>
        Nenhuma venda encontrada para o evento e período selecionados.
      </Typography>
    </Card>
  )
}

function LoadingState() {
  return (
    <Stack spacing={2}>
      <Grid container spacing={1.5}>
        {Array.from({ length: 4 }).map((_, i) => (
          <Grid key={i} xs={12} sm={6} md={3}>
            <Skeleton variant='rounded' height={90} />
          </Grid>
        ))}
      </Grid>
      <Grid container spacing={2}>
        <Grid xs={12} md={7}>
          <Skeleton variant='rounded' height={380} />
        </Grid>
        <Grid xs={12} md={5}>
          <Skeleton variant='rounded' height={380} />
        </Grid>
      </Grid>
      <Skeleton variant='rounded' height={480} />
    </Stack>
  )
}
